import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import {
  createNoteComment,
  listNoteComments,
  mapApiNoteCommentToUi,
} from '../../../services/notes.js';
import { normalizePagedMeta } from '../../../shared/utils/apiResponse.js';
import { apiErrorStatus } from './apiErrorStatus.js';

const PAGE_SIZE = 10;

/**
 * The comment thread of a note (CT-5528): paged, laid out oldest-to-newest.
 *
 * The API answers newest-first per page, so the first page is the latest
 * conversation and "load earlier" walks backwards: each older page is PREPENDED
 * and the newest stays at the bottom, where the input is. Adding a comment
 * reloads the first page — what the user sees is what the server has, never a
 * guess at an id or a timestamp.
 *
 * `submittingRef` is the anti-double-submit guard (see Governance's
 * `SendToReviewModal`): state would arrive a render late, and a fast second
 * click would post twice. A failed comment toasts and returns `false`, so the
 * caller keeps what the user typed.
 */
export function useNoteComments({ noteId } = {}) {
  const { t } = useTranslation('unified-profiles');
  const toast = useToast();

  const [comments, setComments] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(Boolean(noteId));
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fetchIdRef = useRef(0);
  const submittingRef = useRef(false);
  const loadingMoreRef = useRef(false);

  const loadFirstPage = useCallback(async ({ quiet = false } = {}) => {
    if (!noteId) return;
    const requestId = ++fetchIdRef.current;
    // A refresh after posting a comment must reload in place: the skeleton
    // unmounts the thread, drops the earlier pages and scrolls back to the top,
    // leaving the just-added comment at the bottom of the scroll, out of view.
    if (!quiet) setLoading(true);
    setError(null);
    setStatus(null);

    try {
      const { body, meta: rawMeta } = await listNoteComments({ noteId, page: 1, pageSize: PAGE_SIZE });
      if (requestId !== fetchIdRef.current) return;
      setComments((body?.items ?? []).map(mapApiNoteCommentToUi).reverse());
      setMeta(normalizePagedMeta(rawMeta));
    } catch (err) {
      if (requestId !== fetchIdRef.current) return;
      setComments([]);
      setMeta(null);
      setStatus(apiErrorStatus(err));
      setError(err?.message ?? 'Failed to load the comments');
    } finally {
      if (requestId === fetchIdRef.current && !quiet) setLoading(false);
    }
  }, [noteId]);

  useEffect(() => {
    loadFirstPage();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [loadFirstPage]);

  const hasMore = Boolean(meta?.hasNext);

  const loadEarlier = useCallback(async () => {
    // Guard on a ref, not the `loadingMore` state: state lands a render late, so
    // two clicks in the same tick would both fetch the same page and prepend it
    // twice, duplicating the comments.
    if (!noteId || !meta?.hasNext || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    const requestId = fetchIdRef.current;
    setLoadingMore(true);

    try {
      const { body, meta: rawMeta } = await listNoteComments({
        noteId,
        page: (meta.currentPage ?? 1) + 1,
        pageSize: PAGE_SIZE,
      });
      // A note switched (or a posted comment reloaded page 1) underneath us:
      // this page belongs to the old thread, so drop it.
      if (requestId !== fetchIdRef.current) return;
      const older = (body?.items ?? []).map(mapApiNoteCommentToUi).reverse();
      setComments((current) => [...older, ...current]);
      setMeta(normalizePagedMeta(rawMeta));
    } catch (err) {
      if (requestId !== fetchIdRef.current) return;
      toast.error({
        title: t('notes.comments.loadErrorTitle'),
        message: err?.message ?? t('notes.comments.loadErrorMessage'),
      });
    } finally {
      // Always clear: posting a comment bumps `fetchIdRef`, so a clear gated on
      // the request id would strand `loadingMore` at true and permanently
      // disable the "load earlier" control.
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [noteId, meta, toast, t]);

  const addComment = useCallback(
    async (text) => {
      if (!noteId || submittingRef.current) return false;
      submittingRef.current = true;
      setSubmitting(true);

      try {
        await createNoteComment({ noteId, text });
        // Quiet: reload page 1 in place, so the thread does not flash a skeleton
        // and scroll away from the comment that was just posted.
        await loadFirstPage({ quiet: true });
        return true;
      } catch (err) {
        toast.error({
          title: t('notes.comments.addErrorTitle'),
          message: err?.message ?? t('notes.comments.addErrorMessage'),
        });
        return false;
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
    },
    [noteId, loadFirstPage, toast, t],
  );

  return useMemo(
    () => ({
      comments,
      total: meta?.totalItems ?? comments.length,
      hasMore,
      loading,
      loadingMore,
      submitting,
      error,
      accessDenied: status === 403,
      loadEarlier,
      addComment,
      refetch: loadFirstPage,
    }),
    [comments, meta, hasMore, loading, loadingMore, submitting, error, status, loadEarlier, addComment, loadFirstPage],
  );
}

export default useNoteComments;
